import type { StateCell as StateData } from "../../../api/work";

interface Props {
  cellLayout: Array<{ x: number; width: number }>;
  states: StateData[];
  y: number;
  height: number;
  laneColor: string;
}

const WARM = "#d9643a";

/**
 * A compact sparkline above the row of state cells, showing how counts
 * distribute across the workflow. Cells stay uniform height — this plot
 * carries the trend signal.
 */
export function CellTrendSparkline({
  cellLayout, states, y, height, laneColor,
}: Props) {
  if (states.length === 0 || cellLayout.length === 0) return null;

  const counts = states.map((s) => s.count);
  const maxC = Math.max(...counts, 1);

  // A state with no layout slot is dropped rather than drawn at x=0,
  // which is what an `!` here would have produced. `layoutCells` is called
  // with `states.length`, so the filter is unreachable in practice and
  // cheap insurance against it drifting apart.
  const points = states.flatMap((state, i) => {
    const slot = cellLayout[i];
    if (!slot) return [];
    const cx = slot.x + slot.width / 2;
    const cy = y + height - (state.count / maxC) * (height - 2);
    return [{ cx, cy, count: state.count, name: state.name }];
  });
  if (points.length === 0) return null;

  const line = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${p.cx} ${p.cy}`)
    .join(" ");

  // Guarded by the `points.length === 0` return above; the fallbacks keep
  // the type honest without asserting.
  const first = points[0] ?? { cx: 0 };
  const last = points[points.length - 1] ?? first;
  const area = `${line} L ${last.cx} ${y + height} L ${first.cx} ${y + height} Z`;

  const maxIdx = counts.indexOf(maxC);

  return (
    <g>
      <path d={area} fill={laneColor} fillOpacity={0.15} />
      <path
        d={line}
        stroke={laneColor}
        strokeWidth={1.3}
        fill="none"
        strokeLinejoin="round"
      />
      {points.map((p, i) => {
        const isMax = i === maxIdx && p.count > 0;
        const r = isMax ? 2.6 : 2;
        const fill = isMax ? WARM : laneColor;
        return (
          <g key={i}>
            <title>{`${p.name}: ${p.count}`}</title>
            <circle cx={p.cx} cy={p.cy} r={r} fill={fill} />
          </g>
        );
      })}
    </g>
  );
}
